import { splitOrgPrefix } from 'src/app/services/org-url/org-url';

/**
 * What a tab is showing, from its URL (Vincent, 2026-10-22: tabs of the same
 * company named after their page — "Developer", "Software Team", "Tasks"…).
 * The URL is the one reliable thing every page has; names come from the rail's
 * own lists (agents, projects, spaces, chats, topics), passed in as `names`.
 */
export interface TabPageNames {
  /** conversation id → agent / project / space name */
  conversations: ReadonlyMap<string, string>;
  /** chat id → chat title */
  chats: ReadonlyMap<string, string>;
  /** topic (folder) id → topic name */
  topics: ReadonlyMap<string, string>;
}

/** Section pages by path, most specific first — the rail's own labels. */
const SECTIONS: Array<[RegExp, string]> = [
  [/^\/agents\/(hire|new)(\/|$)/, 'org-tabs.page.new-agent'],
  [/^\/agents\/start(\/|$)/, 'sections.agents.title'],
  [/^\/agents\/?$/, 'sidebar.dashboard'],
  [/^\/(projects|teams)\/(hire|new)(\/|$)/, 'org-tabs.page.new-project'],
  [/^\/(projects|teams)(\/|$)/, 'sections.teams.title'],
  [/^\/tasks(\/|$)/, 'tasks.title'],
  [/^\/chat\/topics(\/|$)/, 'sections.topics.title'],
  [/^\/chat(\/|$)/, 'sections.chats.title'],
  [/^\/marketplace(\/|$)/, 'marketplace.title'],
  [/^\/local-models(\/|$)/, 'local_models.title'],
  [/^\/account(\/|$)/, 'account.title'],
  [/^\/messaging(\/|$)/, 'messaging.tab'],
  [/^\/support(\/|$)/, 'menu.support'],
];

/**
 * The page name for a tab URL: the open conversation / chat / topic by name
 * when known, else the section; null when nothing matches (the host shows the
 * company name). `translate` turns a section key into its label.
 */
export function tabPageTitle(url: string | null, names: TabPageNames, translate: (key: string) => string): string | null {
  return tabPageTitleDetail(url, names, translate).title;
}

/**
 * Same, plus whether the name is final: `resolved` is false when the URL
 * points at a conversation / chat / topic whose name is not (yet) in `names`
 * and the section label stands in — a stand-in must never replace a name
 * already resolved for that page (the lists may belong to another org/tab).
 */
export function tabPageTitleDetail(
  url: string | null,
  names: TabPageNames,
  translate: (key: string) => string,
): { title: string | null; resolved: boolean } {
  if (!url) return { title: translate('sidebar.dashboard'), resolved: true };
  const { rest } = splitOrgPrefix(url);
  let path: string;
  let query: URLSearchParams;
  try {
    const parsed = new URL(rest, 'https://x.invalid');
    path = parsed.pathname;
    query = parsed.searchParams;
  } catch {
    return { title: null, resolved: true };
  }
  const conversationId = query.get('conversationId');
  const chat = /^\/chat\/c\/([^/]+)/.exec(path);
  const topic = /^\/chat\/topics\/([^/]+)/.exec(path);
  const named = (conversationId && names.conversations.get(conversationId))
    || (chat && names.chats.get(chat[1]))
    || (topic && names.topics.get(topic[1]));
  if (named) return { title: named, resolved: true };
  const section = SECTIONS.find(([re]) => re.test(path));
  return { title: section ? translate(section[1]) : null, resolved: !conversationId && !chat && !topic };
}
